import {
  ApolloClient,
  from,
  fromPromise,
  HttpLink,
  concat,
  split,
} from "@apollo/client"
import { setContext } from "@apollo/client/link/context"
import { onError } from "@apollo/client/link/error"
import { reloadIfNewBuildAvailable } from "@/v2-ui/utils/utils.build"
import { cache } from "@/v2-backoffice-client/apollo/apollo.cache"
import { handleGraphQLErrors } from "@/v2-backoffice-client/apollo/apollo.error"
import { traceLink } from "@/v2-backoffice-client/apollo/apollo.trace"
import config from "@/v2-backoffice-client/config"
import {
  idToken,
  acquireTokenSilent,
  login,
} from "@/v2-backoffice-client/auth/auth.instance"

const httpLink = new HttpLink({
  uri: config.graphqlUrl,
})

// newRequestId returns a short random identifier that the server
// logs, the router logs, and the frontend console all echo so a
// report from a customer ("got an error at 14:05") can be tied to a
// single concrete GraphQL operation across the full stack. Uses the
// standard RFC4122-style UUID when available, otherwise a 12-char
// base36 fallback for older browsers.
function newRequestId() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID()
  }
  return Math.random().toString(36).slice(2, 14)
}

const authLink = setContext(async (_, { headers }) => {
  let token = idToken

  try {
    const response = await acquireTokenSilent()
    token = response.idToken
  } catch {
    // Silent refresh failed — fall back to cached token.
    // If the token is stale the server will return 401 and
    // the error link will handle re-authentication.
  }

  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : "",
      "x-request-id": newRequestId(),
    },
  }
})

let pendingRefresh: Promise<string> | null = null

function refreshToken() {
  if (pendingRefresh) return pendingRefresh

  // eslint-disable-next-line no-console
  console.warn("Received 401 — attempting token refresh...")

  pendingRefresh = acquireTokenSilent({ forceRefresh: true })
    .then((response) => {
      // eslint-disable-next-line no-console
      console.debug("Token refreshed after 401 — retrying request")
      return response.idToken
    })
    .catch(async () => {
      // eslint-disable-next-line no-console
      console.warn("Token refresh failed after 401, redirecting to login...")
      // A renewal that fails in a tab that has been open since before the
      // last deploy is more often the stale bundle than the session; the
      // reload lands on the current build, which renews for itself.
      if (await reloadIfNewBuildAvailable()) {
        throw new Error(
          "Token refresh failed; reloading onto the current build",
        )
      }
      login().catch((loginError) => {
        // eslint-disable-next-line no-console
        console.error("Login redirect failed:", loginError)
      })
      throw new Error("Token refresh failed")
    })
    .finally(() => {
      pendingRefresh = null
    })

  return pendingRefresh
}

function getHttpStatus(res: Parameters<Parameters<typeof onError>[0]>[0]) {
  const { networkError, operation } = res

  // Check the operation context (set by HttpLink when fetch completes)
  const contextResponse = operation.getContext().response
  if (contextResponse?.status) return contextResponse.status as number

  // Check networkError (ServerError from HttpLink when response body can't be parsed)
  if (networkError && "statusCode" in networkError) {
    return (networkError as { statusCode: number }).statusCode
  }

  return null
}

const errorLink = onError((res) => {
  const { operation, forward } = res

  const status = getHttpStatus(res)

  if (status === 401 && !operation.getContext().hasRetriedAuth) {
    return fromPromise(
      refreshToken().then((token) => {
        const prevHeaders = operation.getContext().headers as
          | Record<string, string>
          | undefined
        operation.setContext({
          hasRetriedAuth: true,
          headers: { ...prevHeaders, authorization: `Bearer ${token}` },
        })
      }),
    ).flatMap(() => forward(operation))
  }

  handleGraphQLErrors(res)
})

const httpTraceLink = split(
  () => config.isInstrumentationEnabled,
  concat(traceLink, httpLink),
  httpLink,
)

const client = new ApolloClient({
  link: from([errorLink, authLink, httpTraceLink]),
  cache,
  // Default errorPolicy: "all" across the board so partial data and
  // GraphQL errors flow together to components. See
  // .cursor/rules/frontend-partial-data-errors.mdc for the rationale
  // and the component-level render pattern.
  defaultOptions: {
    watchQuery: {
      fetchPolicy: "cache-and-network",
      errorPolicy: "all",
    },
    query: {
      errorPolicy: "all",
    },
    mutate: {
      refetchQueries: "active",
      awaitRefetchQueries: false,
      errorPolicy: "all",
    },
  },
})

export default client
